import React, { createContext, useContext, useState, useEffect, useCallback } from "react";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // undefined = still loading; null = not logged in; object = logged in
  const [user, setUser]   = useState(undefined);
  // A share-link visitor: read-only access to ONE client, no account, no role.
  // Kept separate from `user` on purpose — every `user.*` check in the app
  // stays correctly false for them.
  const [share, setShare] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const r = await fetch("/api/auth/me");
      const data = await r.json();
      setUser(data.user || null);
      setShare(data.share || null);
    } catch {
      setUser(null);
      setShare(null);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  async function login(email, password) {
    const res  = await fetch("/api/auth/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || "Login failed");
    setUser(data.user);
    return data;          // includes { redirect, role }
  }

  async function signup({ full_name, email, password, account_type = "family" }) {
    const res  = await fetch("/api/auth/signup", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ full_name, email, password, account_type }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || "Could not create your account");
    setUser(data.user);
    return data;          // includes { redirect, role }
  }

  /**
   * apiFetch — every authenticated call should go through this.
   *
   * Exists because a session can end while a tab sits open: the user signed out
   * in another tab, the cookie expired, a share link was opened elsewhere. The
   * page has no way to learn this until it makes a request, and the raw-fetch
   * pattern turned the resulting 401 into a friendly shrug — three separate
   * debugging sessions have now started with "we couldn't load your profiles"
   * when the real answer was "you are not signed in any more."
   *
   * So a 401 is handled once, here: clear the user, and let the route guard do
   * what it already knows how to do. Anything else is returned to the caller.
   */
  const apiFetch = useCallback(async (input, init) => {
    const res = await fetch(input, init);
    if (res.status === 401) {
      setUser(null);
      setShare(null);
      const err = new Error("Your session has ended. Please sign in again.");
      err.status = 401;
      err.sessionEnded = true;
      throw err;
    }
    return res;
  }, []);

  async function logout() {
    await fetch("/api/auth/logout", { method: "POST" }).catch(() => {});
    await fetch("/api/share/close", { method: "POST" }).catch(() => {});
    setUser(null);
    setShare(null);
  }

  // ── Semantic permissions (two-customer plan §5) ───────────────────────────
  // Ask what the user CAN DO, not what they ARE:
  //     can("manage_account")        not   user.role === "agency_admin"
  //
  // These are ACCOUNT-level capabilities only ("manage_account",
  // "create_client"), because they are the only ones that hold true regardless
  // of which client is on screen. Per-client capabilities ("view_client",
  // "edit_client", "contribute_client", "manage_client") differ per client and
  // arrive on the client payload from the API — never infer them from the role.
  const can = useCallback(
    (capability) => Boolean(user?.capabilities?.includes(capability)),
    [user]
  );

  return (
    <AuthContext.Provider value={{
      user,
      share,
      isShareVisitor: Boolean(share),
      login,
      signup,
      logout,
      refresh,
      apiFetch,
      can,
      accountType: user?.account_type ?? null,
      isFamilyAccount: user?.account_type === "family",
      loading: user === undefined,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
